import {
  IAuthProvider,
  AuthProviderName,
  AuthResult,
  AuthUser,
  MerchantProfile,
  SignUpResult,
} from "./types";

import FirebaseAuthProvider from "./providers/firebase/FirebaseAuthProvider";
import AwsAuthProvider from "./providers/aws/AwsAuthProvider";
import CloudflareAuthProvider from "./providers/cloudflare/CloudflareAuthProvider";

function createProvider(name: AuthProviderName): IAuthProvider {
  switch (name) {
    case "firebase":
      return new FirebaseAuthProvider();
    case "aws":
      return new AwsAuthProvider();
    case "cloudflare":
      return new CloudflareAuthProvider();
    default:
      throw new Error(`Unknown auth provider: ${name}`);
  }
}

const providerName =
  (process.env.REACT_APP_AUTH_PROVIDER as AuthProviderName) ?? "firebase";

const provider = createProvider(providerName);

const authService = {
  signIn: (email: string, password: string): Promise<AuthResult> =>
    provider.signIn(email, password),

  signInWithCustomToken: (
    token: string,
    tenantId?: string | null,
  ): Promise<AuthResult> => provider.signInWithCustomToken(token, tenantId),

  signOut: (): Promise<void> => provider.signOut(),

  signUp: (email: string, password: string): Promise<SignUpResult> =>
    provider.signUp(email, password),

  getCurrentMerchant: (): AuthUser | null => provider.getCurrentMerchant(),

  onAuthStateChanged: (
    callback: (user: AuthUser | null) => void,
  ): (() => void) => provider.onAuthStateChanged(callback),

  getEmailByMerchantId: (merchantId: string): Promise<string | null> =>
    provider.getEmailByMerchantId(merchantId),

  getMerchantProfile: (uid: string): Promise<MerchantProfile> =>
    provider.getMerchantProfile(uid),

  getMustChangePassword: (uid: string): Promise<boolean> =>
    provider.getMustChangePassword(uid),

  changePassword: (newPassword: string): Promise<void> =>
    provider.changePassword(newPassword),

  reauthenticate: (currentPassword: string): Promise<void> =>
    provider.reauthenticate(currentPassword),

  sendPasswordReset: (email: string, continueUrl?: string): Promise<void> =>
    provider.sendPasswordReset(email, continueUrl),

  getToken: (): Promise<string | null> => provider.getToken(),
};

export default authService;
